import { ErrorState, Spinner, EmptyState } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { fmtValue, fmtTime } from '../../broker/bookUtils';
import { RowStatusBadge, SideBadge } from './ManualBadges';

// Shorten long paper order ids (mirrors the global Paper Orderbook) but keep the full id in a tooltip.
function OrderIdCell(v) {
  if (!v || typeof v !== 'string') return fmtValue(v);
  const short = v.length > 14 ? v.slice(0, 14) + '…' : v;
  return <span title={v}>{short}</span>;
}

// Mobile card presentation of one order row — same data/handler as the desktop table's
// columns, just laid out for touch instead of a scrollable table row.
function OrderCard({ row, cancelingId, onCancelOrder }) {
  const busy = row.id === cancelingId;
  return (
    <div className="rounded-lg border border-bg-border bg-bg-surface/40 p-2 mb-1.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-medium text-txt-primary truncate">{fmtValue(row.symbol)}</span>
        <SideBadge side={row.side} className="px-2 py-0.5 text-2xs" />
      </div>

      <div className="flex items-center flex-wrap gap-1 text-2xs font-mono text-txt-muted mb-0.5">
        {row.status
          ? <RowStatusBadge status={row.status} className="px-1.5 py-0.5 text-2xs" />
          : <span className="text-txt-secondary">—</span>}
        {row.type != null && row.type !== '' && <span>· {fmtValue(row.type)}</span>}
        {row.product != null && row.product !== '' && <span>· {fmtValue(row.product)}</span>}
      </div>

      <div className="text-2xs font-mono text-txt-secondary mb-0.5">
        Qty {fmtValue(row.qty)}/{fmtValue(row.filledQty)} · Price {fmtValue(row.price)}
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-1.5">
        {OrderIdCell(row.orderId)} · {fmtTime(row.time)}
      </div>

      {row.canCancel && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onCancelOrder(row)}
          className="w-full min-h-[40px] rounded-md text-xs font-semibold border border-warn/30 bg-warn/10 text-warn hover:bg-warn/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Cancelling…' : 'Cancel'}
        </button>
      )}
    </div>
  );
}

// Orders tab — columns mirror the global Broker → Orderbook (Paper) page, deployment-filtered.
// Rows are already normalized via bookUtils.normalizeOrderRow in ManualStrategyCard.
export default function OrdersTable({ orders = [], loading = false, error = null, onRetry, onCancelOrder, cancelingId = null }) {
  const columns = [
    { key: 'symbol', label: 'Symbol', render: (v) => <span className="text-txt-primary font-medium">{fmtValue(v)}</span> },
    { key: 'side', label: 'Side', render: (v) => <SideBadge side={v} /> },
    { key: 'type', label: 'Type', render: (v) => fmtValue(v), mobileHidden: true },
    { key: 'qty', label: 'Qty', render: (v) => fmtValue(v) },
    { key: 'filledQty', label: 'Filled', render: (v) => fmtValue(v) },
    { key: 'price', label: 'Price', render: (v) => fmtValue(v) },
    { key: 'status', label: 'Status', render: (v) => <RowStatusBadge status={fmtValue(v)} /> },
    { key: 'time', label: 'Time', render: (v) => fmtTime(v), mobileHidden: true },
    { key: 'orderId', label: 'Order ID', render: OrderIdCell, mobileHidden: true },
    // Cancel only the pending qty of a LIVE order. row.canCancel is false for CONFIRMED_FILL/terminal/PAPER.
    ...(onCancelOrder ? [{
      key: '_cancel',
      label: 'Action',
      render: (_v, row) => {
        if (!row?.canCancel) return <span className="text-txt-muted">—</span>;
        const busy = row.id === cancelingId;
        return (
          <button
            type="button"
            disabled={busy}
            onClick={() => onCancelOrder(row)}
            className="mt-action px-2.5 py-1 rounded-md text-2xs font-semibold border border-warn/30 bg-warn/10 text-warn hover:bg-warn/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Cancelling…' : 'Cancel'}
          </button>
        );
      },
    }] : []),
  ];

  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  return (
    <>
      {/* Desktop — compact local table (not the shared ui/DataTable), matching the Positions
          table's header/row padding so switching Positions → Orders → Trade Book has no
          visible density jump. No other DataTable-based screen is affected. Bounded to ~6 data
          rows (a single overflow-auto container handles vertical + horizontal scroll together,
          so a 7th+ row scrolls internally without any page-level horizontal overflow); the sticky,
          shaded header (bg-bg-hover — visibly distinct from the plain row background, opaque so
          scrolling row text never shows through) stays pinned during that scroll. Rows are never
          fetched/dropped for this — orders.length is unaffected. */}
      <div className="hidden lg:block">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size={20} />
          </div>
        ) : !orders.length ? (
          <EmptyState title="No orders" />
        ) : (
          <div className="overflow-auto max-h-[14.5rem]">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={cn(
                        'sticky top-0 z-10 bg-bg-hover border-b border-bg-border',
                        'text-left py-1.5 px-2.5 text-txt-label uppercase tracking-widest text-2xs font-semibold whitespace-nowrap',
                        col.mobileHidden && 'hidden sm:table-cell',
                      )}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((row, i) => (
                  <tr key={row.id ?? i} className="border-b border-bg-border/50">
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'py-2 px-2.5 text-txt-secondary font-mono',
                          col.mobileHidden && 'hidden sm:table-cell',
                        )}
                      >
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="lg:hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={20} />
          </div>
        ) : !orders.length ? (
          <EmptyState title="No orders" />
        ) : (
          orders.map((row, i) => (
            <OrderCard key={row.id ?? i} row={row} cancelingId={cancelingId} onCancelOrder={onCancelOrder} />
          ))
        )}
      </div>
    </>
  );
}
